import {
  actionColumn,
  BoundNumberField,
  BoundTextField,
  Button,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  numericColumn,
  SelectField,
  simpleHeader,
  useComputed,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { action } from "mobx";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { emptyCellDash, FormActions, priceCell } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import { Maybe, SaveCommitmentInput } from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PaymentScheduleTable_CommitmentLikeFragment,
  PaymentScheduleTable_DrawFragment,
  PaymentScheduleTable_PlanTaskFragment,
  usePaymentScheduleTaskOptionsQuery,
} from "src/routes/pdfs/CommitmentOrChangeOrderPdf/components/PaymentScheduleTable.generated";
import { createTaskDetailsPageUrl } from "src/RouteUrls";

type PaymentScheduleTableProps = {
  commitment: PaymentScheduleTable_CommitmentLikeFragment;
  /** Defaults to "view". "edit" only takes effect when `onSubmit` is provided. */
  mode?: "view" | "edit";
  /** Shows the "Task status" column in the read-only table. Likely hidden in PDF. */
  showStatus?: boolean;
  /** Shows a "Payment Schedule" heading on the actions row, matching the other tables on the page. */
  showTitle?: boolean;
  /** When provided, the table is editable and calls this with the draws input on save. */
  onSubmit?: (input: PaymentScheduleInput) => Promise<void>;
  /** Rows can't be added or removed, and only the % is editable (e.g. a change order's copied draws). */
  fixedDraws?: boolean;
};

/** The draws input shared by `SaveCommitmentInput` and `SaveCommitmentChangeOrderInput`. */
type PaymentScheduleInput = Pick<SaveCommitmentInput, "id" | "depositInBasisPoints" | "draws">;

export function PaymentScheduleTable(props: PaymentScheduleTableProps) {
  const { commitment, mode = "view", onSubmit, showStatus, showTitle, fixedDraws = false } = props;
  const { committedInCents, canEditDraws } = commitment;
  const [editing, setEditing] = useState(false);

  // Draws are tied to project plan tasks whose budget item matches one of the line items' budget items.
  const itemIds = useMemo(
    () => commitment.lineItems.map((li) => li.projectItem.item.id).unique(),
    [commitment.lineItems],
  );
  const { data: taskOptionsData } = usePaymentScheduleTaskOptionsQuery({
    variables: { projectId: commitment.project.id, budgetItemIds: itemIds },
    // Options are only needed when editing tasks; skip in the read-only PDF/view path and for fixed draws.
    skip: !editing || fixedDraws || itemIds.isEmpty,
  });
  const taskOptions = useMemo(() => taskOptionsData?.planTasks.entities ?? [], [taskOptionsData]);

  const formState = useFormState({
    config: formConfig,
    init: { input: commitment, map: (c) => mapToForm(c, fixedDraws) },
    readOnly: !editing,
  });
  const columns = useMemo(
    () =>
      createColumns(editing, commitment.project.id, committedInCents, taskOptions, formState, showStatus, fixedDraws),
    [editing, commitment.project.id, committedInCents, showStatus, taskOptions, formState, fixedDraws],
  );
  const rows = useComputed(() => createRows(formState), [formState]);

  return (
    <div>
      {/* Hide the whole row when there's nothing to show (view mode without a title, e.g. the PDF). */}
      <div css={Css.df.jcsb.aic.gap1.mb1.if(mode === "view" && !showTitle).dn.$}>
        {/* Title sits on the same row as the actions, left-aligned, matching the other tables on this page. */}
        {showTitle ? <h2 css={Css.mdSb.$}>Payment Schedule</h2> : <div />}
        {/* Actions live on the right and are hidden in view mode (e.g. the PDF). */}
        <div css={Css.df.aic.gap1.if(mode === "view").dn.$}>
          {!fixedDraws && (
            <Button
              variant="tertiary"
              icon="plus"
              label="Add Draw"
              disabled={!editing || disableBasedOnPotentialOperation(canEditDraws)}
              onClick={() => {
                // New lines start as a draw; the user can switch them to a deposit in Bill Terms.
                formState.draws.add({
                  id: undefined,
                  description: "",
                  amountInBasisPoints: 0,
                  task: null,
                  isDeposit: false,
                });
              }}
            />
          )}
          <FormActions
            formState={formState}
            mode={editing ? "update" : "read"}
            // Block entering edit mode when the PO isn't in a state where draws can be changed.
            editDisableReason={
              !canEditDraws.allowed ? canEditDraws.disabledReasons.map((r) => r.message).join(", ") : undefined
            }
            onEdit={() => setEditing(true)}
            onCancel={() => {
              formState.revertChanges();
              setEditing(false);
            }}
            onSave={async () => {
              await onSubmit?.(mapToInput(commitment.id, formState));
              formState.commitChanges();
              setEditing(false);
            }}
          />
        </div>
      </div>
      <div css={Css.mb2.$}>
        <GridTable as="table" columns={columns} rows={rows} />
      </div>
    </div>
  );
}

type HeaderRow = { kind: "header" };
type DrawLineRow = { kind: "draw"; data: ObjectState<DrawInput> };
type TotalRow = { kind: "total"; data: undefined };
type Row = HeaderRow | DrawLineRow | TotalRow;

type TaskOption = Pick<PaymentScheduleTable_PlanTaskFragment, "id" | "name" | "status">;

function createColumns(
  isEdit: boolean,
  projectId: string,
  committedInCents: number,
  taskOptions: TaskOption[],
  formState: ObjectState<FormValue>,
  showStatus: boolean | undefined,
  fixedDraws: boolean,
): GridColumn<Row>[] {
  // Fixed draws can't change their bill terms, task or description, only their %.
  const isEditRows = isEdit && !fixedDraws;
  return [
    column<Row>({
      header: () => "Bill Terms",
      draw: (row) =>
        isEditRows ? (
          <SelectField
            label="Bill Terms"
            labelStyle="hidden"
            value={row.isDeposit.value ? "deposit" : "draw"}
            options={billTermsOptions}
            onSelect={(value) => {
              const isDeposit = value === "deposit";
              row.isDeposit.set(isDeposit);
              // Deposits don't have an associated task, so clear it on switch.
              if (isDeposit) row.task.set(null);
            }}
            // Only one deposit is allowed (it maps to the commitment's single depositInBasisPoints).
            disabledOptions={
              formState.draws.rows.some((d) => d.isDeposit.value)
                ? [{ value: "deposit", reason: "A deposit already exists" }]
                : []
            }
          />
        ) : row.isDeposit.value ? (
          "Deposit"
        ) : (
          "Draw"
        ),
      total: emptyCell,
    }),
    column<Row>({
      header: () => "Associated Task",
      draw: (row) => {
        if (isEditRows) {
          return (
            <SelectField
              label="Associated Task"
              labelStyle="hidden"
              value={row.task.value?.id}
              // Store the whole task fragment so the view/status columns can read it.
              onSelect={(_, task) => row.task.set(task ?? null)}
              options={taskOptions}
              placeholder="Select a task"
              // Deposits don't have an associated task.
              disabled={!!row.isDeposit.value && "Deposits do not have an associated task"}
              fieldDecoration={(t) => <StatusIndicator status={t.status.code} />}
              errorMsg={row.task.touched ? row.task.errors.join(", ") : undefined}
              getOptionMenuLabel={(t) => <div css={Css.df.aic.gap1.$}>{t.name}</div>}
              // Don't let two draws point at the same task.
              disabledOptions={formState.draws.rows
                .map((d) => d.task.value?.id)
                .compact()
                .filter((id) => id !== row.task.value?.id)
                .map((id) => ({ value: id, reason: "Task already assigned to another draw" }))}
            />
          );
        }

        // View mode: deposits have no task; otherwise render the stored task's name + link.
        if (row.isDeposit.value) return emptyCellDash;
        const task = row.task.value;
        return task ? (
          <Link to={createTaskDetailsPageUrl(projectId, task.id)}>
            <div css={Css.hPx(32).df.aic.$}>{task.name}</div>
          </Link>
        ) : (
          emptyCellDash
        );
      },
      total: emptyCell,
    }),
    ...(showStatus
      ? [
          column<Row>({
            header: () => "Task status",
            draw: (row) => {
              const task = row.task.value;
              // Return null (renders an empty cell) instead of `emptyCell`, which Observer can't render.
              if (!task) return null;
              return (
                <div css={Css.df.aic.gap1.$}>
                  <StatusIndicator status={task.status.code} />
                  <div css={Css.hPx(32).df.aic.$}>{task.status.name}</div>
                </div>
              );
            },
            total: emptyCell,
          }),
        ]
      : []),
    column<Row>({
      header: () => "Description",
      draw: (row) => <BoundTextField field={row.description} labelStyle="hidden" readOnly={fixedDraws || undefined} />,
      total: emptyCell,
    }),
    column<Row>({
      header: () => "% of Contract",
      draw: (row) => (
        <BoundNumberField
          type="basisPoints"
          labelStyle="hidden"
          field={row.amountInBasisPoints}
          numFractionDigits={2}
          truncate
          // Show this row's own error, otherwise fall back to the list-level total rule.
          // Without surfacing the list error here, Save silently no-ops when the total isn't 100%.
          errorMsg={
            row.amountInBasisPoints.touched
              ? row.amountInBasisPoints.errors.join(", ") || formState.draws.errors.join(", ")
              : undefined
          }
        />
      ),
      total: emptyCell,
    }),
    numericColumn<Row>({
      header: () => "Cost",
      draw: (row) =>
        priceCell({ id: "cost", valueInCents: (committedInCents * (row.amountInBasisPoints.value ?? 0)) / 10000 }),
      total: () => priceCell({ id: "cost", valueInCents: committedInCents }),
    }),
    ...(fixedDraws
      ? []
      : [
          actionColumn<Row>({
            header: emptyCell,
            draw: (row) =>
              isEdit ? (
                <IconButton
                  icon="trash"
                  data-testid="trash"
                  onClick={action(() => formState.draws.remove(row.value))}
                />
              ) : (
                emptyCell
              ),
            total: emptyCell,
            w: "60px",
          }),
        ]),
  ];
}

// Bill Terms options; the value maps to `isDeposit` (a line is either a deposit or a draw).
type BillTerm = "draw" | "deposit";
const billTermsOptions: { id: BillTerm; name: string }[] = [
  { id: "draw", name: "Draw" },
  { id: "deposit", name: "Deposit" },
];

function createRows(formState: ObjectState<FormValue>): GridDataRow<Row>[] {
  const rows = formState.draws.rows.map((draw, idx) => ({
    kind: "draw" as const,
    id: draw.value.id || `draw-${idx}`,
    data: draw,
  }));
  return [simpleHeader, ...rows, { id: "total", kind: "total" as const, data: undefined }];
}

type DrawInput = Omit<PaymentScheduleTable_DrawFragment, "id" | "task" | "amountInBasisPoints"> & {
  // Nullable so unsaved rows (new draws, the deposit pseudo-row) can leave it undefined.
  id: Maybe<string>;
  amountInBasisPoints: Maybe<number>;
  task: Maybe<TaskOption>;
  isDeposit?: boolean;
};

type FormValue = { draws: DrawInput[] };
const formConfig: ObjectConfig<FormValue> = {
  draws: {
    type: "list",
    // The deposit plus all draws must add up to the full contract (100%).
    rules: [
      ({ value: rows }) => {
        const total = rows.reduce((acc, r) => acc + (r.amountInBasisPoints.value ?? 0), 0);
        return total === 100_00 ? undefined : "Deposit and draws must total 100%";
      },
    ],
    config: {
      id: { type: "value" },
      description: { type: "value" },
      amountInBasisPoints: { type: "value", rules: [required] },
      task: {
        type: "value",
        // Draws need an associated task; deposits don't.
        rules: [
          (opts) => {
            const row = (opts as unknown as { object: ObjectState<DrawInput> }).object;
            return !row.isDeposit.value && !opts.value ? "Draws must have an associated task" : undefined;
          },
        ],
      },
      isDeposit: { type: "value" },
    },
  },
};

function mapToForm(commitment: PaymentScheduleTable_CommitmentLikeFragment, fixedDraws: boolean): FormValue {
  // Fixed draws can't add a deposit back, so keep a deposit re-allocated to 0% as a row rather than dropping it.
  const hasDeposit = fixedDraws ? commitment.depositInBasisPoints != null : !!commitment.depositInBasisPoints;
  return {
    draws: [
      ...(hasDeposit
        ? [
            // Append a deposit row if there is a deposit.
            {
              id: undefined,
              description: null,
              amountInBasisPoints: commitment.depositInBasisPoints,
              task: null,
              isDeposit: true,
            },
          ]
        : []),
      ...commitment.draws.map((d) => ({
        id: d.id,
        description: d.description,
        amountInBasisPoints: d.amountInBasisPoints,
        task: d.task,
        isDeposit: false,
      })),
    ],
  };
}

function mapToInput(commitmentId: string, formState: ObjectState<FormValue>): PaymentScheduleInput {
  const [deposits, draws] = formState.draws.value.partition((r) => !!r.isDeposit);
  return {
    id: commitmentId,
    depositInBasisPoints: deposits.nonEmpty ? deposits.first!.amountInBasisPoints : undefined,
    draws: draws.map((d) => ({
      id: d.id || undefined, // pass undefined for new draw rows (where id is an empty string)
      description: d.description,
      amountInBasisPoints: d.amountInBasisPoints,
      taskId: d.task?.id,
    })),
  };
}
